import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '@/features/snackbar';

import { getQueryKey as getCompanyNotesQueryKey } from '../useCompanyNotes';
import { getQueryKey as getUserNotesQueryKey } from '../useUserNotes';
import type {
  CompanyNotesQueryClientData,
  NoteFragmentType,
} from '../useCompanyNotes';
import { queryFn } from './queryFn';

type Options = {
  onSuccess?: (data: NoteFragmentType) => void;
};

export const useCreateCompanyNote = (options: Options = {}) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: queryFn,
    onSuccess: (data, { companyId }) => {
      const COMPANY_NOTES_QUERY_KEY = getCompanyNotesQueryKey(companyId);
      queryClient.setQueryData<CompanyNotesQueryClientData>(
        COMPANY_NOTES_QUERY_KEY,
        (state) => {
          if (typeof state === 'undefined') return state;
          return [...state, data];
        }
      );
      addSnackbar({
        type: 'positive',
        lifeTime: 1000,
        message: 'Note successfully created',
      });
      options.onSuccess?.(data);
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message: 'Failed to create note',
      });
    },
    onSettled: (data, __, { companyId }) => {
      queryClient.invalidateQueries(getCompanyNotesQueryKey(companyId));
      if (typeof data !== 'undefined') {
        queryClient.invalidateQueries(getUserNotesQueryKey(data.userId));
      }
    },
  });
};
